"use client"

import { actionLoginUser } from "@/app/login/actions"
import { BlueButton } from "@/components/generic/BlueButton"
import { isDJUIEnabled } from "@/lib/featureFlags.utils"
import { Anchor, Box, PasswordInput, Text, TextInput } from "@mantine/core"
import { useForm } from "@mantine/form"
import { notifications } from "@mantine/notifications"
import { IconLogin } from "@tabler/icons-react"
import { useState } from "react"
import { z } from "zod"

export function LoginBox() {
  const isDJUIFeatureEnabled = isDJUIEnabled()

  const [isLoading, setIsLoading] = useState(false)
  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      email: "",
      password: "",
    },
    validate: {
      email: (value) => {
        const safeEmail = z.string().email().safeParse(value)
        if (!safeEmail.success) {
          return "Invalid email"
        }
        return null
      },
      password: (value) => {
        const safePassword = z.string().trim().min(1).safeParse(value)
        if (!safePassword.success) {
          return "Password must have some characters"
        }
        return null
      },
    },
  })

  async function handleLogin() {
    if (isLoading) return
    form.validate()

    if (form.isValid() === false) return

    const values = form.getTransformedValues()

    try {
      setIsLoading(true)
      await actionLoginUser({
        email: values.email,
        password: values.password,
      })

      notifications.show({
        title: "Logged in",
        message: "You have been successfully logged in.",
        color: "green",
        autoClose: 3000,
      })
    } catch (err) {
      notifications.show({
        title: "Error while logging in",
        message:
          (err as any)?.message ??
          "Try again in a few seconds or reach your administrator for help.",
        color: "red",
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (isDJUIFeatureEnabled) {
    return (
      <Box
        px={"lg"}
        mt={"lg"}
        className="space-y-3"
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault()
            handleLogin()
          }
        }}
      >
        <Text>
          Please login in the Ring.io Chrome extension to use this webapp
        </Text>
        <TextInput
          disabled={isLoading}
          placeholder="yourmail@domain.com"
          key={form.key("email")}
          {...form.getInputProps("email")}
        />
        <PasswordInput
          disabled={isLoading}
          placeholder="Password"
          key={form.key("password")}
          {...form.getInputProps("password")}
        />
        <Box className="flex w-full flex-col space-y-3 pt-2">
          <BlueButton
            loading={isLoading}
            fullWidth
            leftSection={<IconLogin className="size-4" />}
            onClick={handleLogin}
          >
            Sign In
          </BlueButton>
          <Anchor
            href="/recover-password"
            className="text-center !text-r-blue-darkest"
            mt="xs"
          >
            <Text size="sm">Forgot your password?</Text>
          </Anchor>
        </Box>
      </Box>
    )
  }

  return (
    <Box px={"lg"} className="mt-16 flex flex-row items-center justify-center">
      <Text className="!text-center">
        Please login in the Ring.io Chrome extension to use this webapp
      </Text>
    </Box>
  )
}
